import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import LoadingPanel from "../../custom-components/CustomLoader";
import { useBreakpoint } from "../../custom-hooks/useBreakpoint";

interface CollectionResponse<T> {
    rows: T[];
    totalRows: number;
    totalPages?: number;
    currentPage?: number;
    limit?: number;
}

interface SimilarItemsSectionProps<T> {
    currentItemId: string;
    fetchItems: (page: number, limit: number) => Promise<CollectionResponse<T>>;
    renderCard: (item: T) => ReactNode;
    translationKey: string;
    queryKeyBase: string;
}

function SimilarItemsSection<T>({
    currentItemId,
    fetchItems,
    renderCard,
    translationKey,
    queryKeyBase,
}: SimilarItemsSectionProps<T>) {
    const { t, i18n } = useTranslation();
    const { isTablet } = useBreakpoint();
    const itemCount = isTablet ? 4 : 3;

    const { data: similarItems = [], isLoading } = useQuery({
        queryKey: [queryKeyBase, "similar", currentItemId, i18n.language, isTablet],
        queryFn: async () => {
            const limit = isTablet ? 4 : 3;
            const result = await fetchItems(1, limit + 1);
            const filtered = result.rows.filter((item) => (item as { id?: string }).id !== currentItemId);
            return filtered.slice(0, itemCount);
        },
        enabled: !!currentItemId,
    });

    if (isLoading) {
        return <LoadingPanel isVisible={isLoading} />;
    }

    if (similarItems.length === 0) {
        return null;
    }

    return (
        <section className="w-full bg-background-100 pt-[100px] pb-[100px]">
            <div className="max-w-[1280px] mx-auto">
                <h2 className="font-geologica font-medium text-[32px] uppercase text-primary-400 mb-[50px]">
                    {t(translationKey)}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lap:grid-cols-3 gap-5">
                    {similarItems.map((item) => (
                        <div key={(item as { id?: string }).id}>{renderCard(item)}</div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default SimilarItemsSection;
